import { imgixHost } from 'src/config'
import { TYPES as CASH_COUNT_TYPES } from 'src/models/cash/CashCount'
import Location from 'src/models/location/Location'
import Model, { encoders, ID, JsonArray, JsonObject, MODEL_LEVEL } from 'src/models/model'
import Archivable from 'src/models/model/concerns/Archivable'
import { SECTION_TYPES } from 'src/models/page/Section'
import RegisterPage from 'src/models/register/RegisterPage'
import SignageMediaClass from 'src/models/signage/SignageMediaClass'

export const CUSTOMER_DISPLAY_IDLE_TYPES = {
  SPLASH_IMAGE: 'splash-image',
  MEDIA: 'media',
  SIGNAGE: 'signage'
}
CUSTOMER_DISPLAY_IDLE_TYPES.ALL = _.values(CUSTOMER_DISPLAY_IDLE_TYPES)
CUSTOMER_DISPLAY_IDLE_TYPES.MEDIAS = [CUSTOMER_DISPLAY_IDLE_TYPES.MEDIA, CUSTOMER_DISPLAY_IDLE_TYPES.SIGNAGE]

export const MOBILE_HOME_VIEW_TYPES = {
  REGISTER: 'register',
  DELIVERY: 'delivery'
}
MOBILE_HOME_VIEW_TYPES.ALL = _.values(MOBILE_HOME_VIEW_TYPES)

export const LOGIN_DESTINATIONS = {
  REGISTER: {
    value: 'register',
    label: 'Register',
    icon: 'point_of_sale'
  },
  TIME_CARD: {
    value: 'time-card',
    label: 'Time Card',
    icon: 'alarm'
  }
}
export const LOGIN_DESTINATIONS_TYPE_MAP = {
  register: LOGIN_DESTINATIONS.REGISTER,
  'time-card': LOGIN_DESTINATIONS.TIME_CARD
}

export const APPLICATION_TYPES = {
  REGISTER: 'register',
  KIOSK: 'kiosk'
}

export const KIOSK_HOMEPAGE_ADVERTISING_DISPLAY_TYPES = {
  PLAYING_TODAY_CAROUSEL: 'playing-today-carousel',
  FULL_SCREEN_CAROUSEL: 'full-screen-carousel',
  SIGNAGE: 'signage'

}

export default class RegisterType extends Model {
  static name = 'RegisterType'

  static isConfiguration = true

  static versioned = true

  static level = MODEL_LEVEL.SITE

  static employeeRequiredPermissionCategories = [Model.permissionCategories.HardwareAndRegisterStation]

  static define () {
    this.concerns = [Archivable]

    this.fields = {
      id: {
        type: ID,
        required: true
      },
      name: {
        type: String,
        required: true
      },
      applicationType: {
        type: String,
        required: true
      },
      printTemplateConfiguration: {
        type: JsonObject,
        mergeDefault: true,
        default: () => ({
          ticketTemplateId: null,
          giftCardTemplateId: null,
          voucherTemplateId: null,
          overrideTicketTemplate: false,
          overrideGiftCardTemplate: false,
          overrideVoucherTemplate: false,
          useThermalReceipt: false,
          thermalReceiptPrintLogo: false
        })
      },
      keepShowingSelection: {
        type: Boolean,
        default: false
      },
      showCounterPickupOption: {
        type: Boolean,
        default: false
      },
      showExpressPickupOption: {
        type: Boolean,
        default: false
      },
      showTheaterSeatDeliveryOption: {
        type: Boolean,
        default: false
      },
      showTableDeliveryOption: {
        type: Boolean,
        default: false
      },
      showFavoritesSection: {
        type: Boolean,
        default: true
      },
      autoAddUsualOrder: {
        type: Boolean,
        default: false
      },
      deliveryTypes: {
        type: JsonArray,
        default: () => ([])
      },
      deliveryTypePriority: {
        type: JsonArray,
        default: () => ([])
      },
      printTemplates: {
        type: JsonArray,
        default: () => ([])
      },
      requireAuthorizationForPrintingItems: {
        type: Boolean,
        default: true
      },
      groupTicketPrintingEnabled: {
        type: Boolean,
        default: false
      },
      location: {
        type: Location,
        encode: encoders.hasOne,
        reference: true,
        default: null
      },
      stripeTerminalLocationId: {
        type: String,
        default: null
      },
      stripeOnReaderTips: {
        type: Boolean,
        default: false
      },
      quickCashAmountOptions: {
        type: JsonArray,
        default: () => ([20, 10, 5, 1])
      },
      registerOptions: {
        type: JsonObject,
        default () {
          return {
            view_port_zoom: null,
            auto_logout: true,
            auto_logout_minutes: 5,
            allow_cash_payments: true,
            require_cash_session_for_transactions: true,
            enable_restricted_user_cash_sessions: false,
            enable_automatic_cash_sessions: true,
            require_user_cash_session_for_tabs: false,
            automatic_cash_sessions_interval_days: 1,
            allow_tab_orders: false,
            allow_invoice_orders: false,
            show_tipping: false,
            show_quick_cash: false,
            prompt_for_delivery_on_mobile: false,
            mobile_homepage_view_type: MOBILE_HOME_VIEW_TYPES.REGISTER,
            prompt_for_delivery_position_owner: false,
            allow_start_tab_with_delivery_info: false,
            auto_print_receipts: false,
            allow_register_delivery_type: true,
            enable_confirm_selected_user: true,
            cash_count_requires_authorization: true,
            show_add_saved_card_button: false,
            use_preauth_for_order_saved_cards: false,
            enable_sms_receipts: false,
            enable_email_receipts: false
          }
        },
        mergeDefault: true
      },
      kioskOptions: {
        type: JsonObject,
        mergeDefault: true,
        default: () => ({
          homepage_signage_url: 'https://google.com',
          homepage_carousel_include_trailer_autoplay: true,
          showings_display_type: SECTION_TYPES.SHOWTIMES,
          show_food_and_beverage_on_homepage: false,
          allow_food_and_beverage_ordering: true,
          disable_ticket_printing: false,
          show_ticket_purchase_on_homepage: true,
          welcome_screen_custom_image: null,
          welcome_screen_show_hero_text: true,
          welcome_screen_show_logo: true,
          welcome_screen_show_cta_button: true,
          show_activities_purchase_on_homepage: false
        })
      },
      cashCountType: {
        type: String,
        default: CASH_COUNT_TYPES.CASH_TRAY
      },
      styles: {
        type: String,
        default: '',
        excludeFromDefault: true
      },
      transformedStyles: {
        type: String,
        excludeFromDefault: true,
        output: true
      },
      defaultLoginDestination: {
        type: String,
        default: LOGIN_DESTINATIONS.REGISTER.value
      },
      loginDestinations: {
        type: JsonArray,
        default: () => ([LOGIN_DESTINATIONS.REGISTER.value])
      },
      toggleLoginDestinationEnabled: {
        type: Boolean,
        default: true
      },
      searchOptions: {
        type: JsonObject,
        default: () => ({
          invoice_order_search_enabled: true,
          invoice_user_search_enabled: true,
          employee_user_search_enabled: false
        })
      },
      customerDisplayIdleType: {
        type: String,
        default: CUSTOMER_DISPLAY_IDLE_TYPES.SPLASH_IMAGE
      },
      customerDisplayMediaClass: {
        type: SignageMediaClass,
        reference: true,
        encode: encoders.hasOne
      },
      registerPages: {
        type: [RegisterPage],
        required: true,
        default: () => ([]),
        allowDestroy: true
      },
      stripeTerminalLocationOptions: {
        type: JsonArray,
        default: () => ([]),
        output: true,
        excludeFromDefault: true
      }
    }

    this.computed = {
      deliveryTypeOptions () {
        const currentSite = this.configuration.currentSite
        if (!currentSite) return []

        return _.concat(this.arrayToOptions(_.compact([
          ((currentSite.counterPickupEnabled && this.showCounterPickupOption) ? 'counter-pickup' : null),
          ((currentSite.expressPickupEnabled && this.showExpressPickupOption) ? 'express-pickup' : null),
          ((currentSite.tableDeliveryEnabled && this.showTableDeliveryOption) ? 'table' : null),
          ((currentSite.theaterSeatDeliveryEnabled && this.showTheaterSeatDeliveryOption) ? 'theater-seat' : null)
        ])), _.map(
          _.filter(this.configuration?.deliveryTypes?.data, deliveryType => {
            return deliveryType.enable && _.includes(this.deliveryTypes, deliveryType.id)
          }),
          deliveryType => {
            return { label: deliveryType.name, value: deliveryType.id.toString() }
          }))
      },
      loginDestinationOptions () {
        return _.filter(LOGIN_DESTINATIONS, loginDestination => this.loginDestinations.includes(loginDestination.value))
      },
      kioskBackgroundImageUrl () {
        if (!this.kioskOptions?.background_image) return
        return `${imgixHost}/${this.kioskOptions.background_image}?max-w=1000&auto=format,compress`
      },
      kioskWelcomeScreenCustomImageUrl () {
        if (!this.kioskOptions?.welcome_screen_custom_image) return
        return `${imgixHost}/${this.kioskOptions.welcome_screen_custom_image}?max-w=2000&auto=format,compress`
      },
      kioskViewPortZoom () {
        return this.kioskOptions?.view_port_zoom
      }
    }

    this.fragments = {
      cashCountOptions: ['id', 'cashCountType', 'registerOptions']
    }
  }

  get registerViewPortZoom () {
    return this.registerOptions?.view_port_zoom
  }

  set registerViewPortZoom (value) {
    this.registerOptions.view_port_zoom = value
  }

  get autoLogout () {
    return this.registerOptions.auto_logout
  }

  set autoLogout (value) {
    this.registerOptions.auto_logout = value
  }

  get autoLogoutMinutes () {
    return this.registerOptions.auto_logout_minutes
  }

  set autoLogoutMinutes (value) {
    this.registerOptions.auto_logout_minutes = value
  }

  get allowCashPayments () {
    return this.registerOptions.allow_cash_payments
  }

  set allowCashPayments (value) {
    this.registerOptions.allow_cash_payments = value
  }

  get requireCashSessionForTransactions () {
    return this.registerOptions.require_cash_session_for_transactions
  }

  set requireCashSessionForTransactions (value) {
    this.registerOptions.require_cash_session_for_transactions = value
  }

  get enableRestrictedUserCashSessions () {
    return this.registerOptions.enable_restricted_user_cash_sessions
  }

  set enableRestrictedUserCashSessions (value) {
    this.registerOptions.enable_restricted_user_cash_sessions = value
  }

  get enableAutomaticCashSessions () {
    return this.registerOptions.enable_automatic_cash_sessions
  }

  set enableAutomaticCashSessions (value) {
    this.registerOptions.enable_automatic_cash_sessions = value
  }

  get automaticCashSessionsIntervalDays () {
    return this.registerOptions.automatic_cash_sessions_interval_days
  }

  set automaticCashSessionsIntervalDays (value) {
    this.registerOptions.automatic_cash_sessions_interval_days = value
  }

  get allowTabOrders () {
    return this.registerOptions.allow_tab_orders
  }

  set allowTabOrders (value) {
    this.registerOptions.allow_tab_orders = value
  }

  get allowInvoiceOrders () {
    return this.registerOptions.allow_invoice_orders
  }

  set allowInvoiceOrders (value) {
    this.registerOptions.allow_invoice_orders = value
  }

  get showTipping () {
    return this.registerOptions.show_tipping
  }

  set showTipping (value) {
    this.registerOptions.show_tipping = value
  }

  get showQuickCash () {
    return this.registerOptions.show_quick_cash
  }

  set showQuickCash (value) {
    this.registerOptions.show_quick_cash = value
  }

  get promptForDeliveryOnMobile () {
    return this.registerOptions.prompt_for_delivery_on_mobile
  }

  set promptForDeliveryOnMobile (value) {
    this.registerOptions.prompt_for_delivery_on_mobile = value
  }

  get mobileHomepageViewType () {
    return this.registerOptions.mobile_homepage_view_type
  }

  set mobileHomepageViewType (value) {
    this.registerOptions.mobile_homepage_view_type = value
  }

  get promptForDeliveryPositionOwner () {
    return this.registerOptions.prompt_for_delivery_position_owner
  }

  set promptForDeliveryPositionOwner (value) {
    this.registerOptions.prompt_for_delivery_position_owner = value
  }

  get useDeliveryMobileHomepage () {
    return this.mobileHomepageViewType === MOBILE_HOME_VIEW_TYPES.DELIVERY
  }

  get allowStartTabWithDeliveryInfo () {
    return this.registerOptions.allow_start_tab_with_delivery_info
  }

  set allowStartTabWithDeliveryInfo (value) {
    this.registerOptions.allow_start_tab_with_delivery_info = value
  }

  get autoPrintReceipts () {
    return this.registerOptions.auto_print_receipts
  }

  set autoPrintReceipts (value) {
    this.registerOptions.auto_print_receipts = value
  }

  get allowRegisterDeliveryType () {
    return this.registerOptions.allow_register_delivery_type
  }

  set allowRegisterDeliveryType (value) {
    this.registerOptions.allow_register_delivery_type = value
  }

  get enableConfirmSelectedUser () {
    return this.registerOptions.enable_confirm_selected_user
  }

  set enableConfirmSelectedUser (value) {
    this.registerOptions.enable_confirm_selected_user = value
  }

  get requireUserCashSessionsForTabs () {
    return this.registerOptions.require_user_cash_session_for_tabs
  }

  set requireUserCashSessionsForTabs (value) {
    this.registerOptions.require_user_cash_session_for_tabs = value
  }

  get cashCountRequiresAuthorization () {
    return this.registerOptions.cash_count_requires_authorization
  }

  set cashCountRequiresAuthorization (value) {
    this.registerOptions.cash_count_requires_authorization = value
  }

  get allowKioskFoodAndBeverageOrdering () {
    return this.kioskOptions.allow_food_and_beverage_ordering
  }

  set allowKioskFoodAndBeverageOrdering (value) {
    this.kioskOptions.allow_food_and_beverage_ordering = value
  }

  get showAddSavedCardButton () {
    return this.registerOptions.show_add_saved_card_button
  }

  set showAddSavedCardButton (value) {
    this.registerOptions.show_add_saved_card_button = value
  }

  get usePreauthForOrderSavedCards () {
    return this.registerOptions.use_preauth_for_order_saved_cards
  }

  set usePreauthForOrderSavedCards (value) {
    this.registerOptions.use_preauth_for_order_saved_cards = value
  }

  get enableSmsReceipts () {
    return this.configuration.currentCircuit.enableSms && this.registerOptions.enable_sms_receipts
  }

  set enableSmsReceipts (value) {
    this.registerOptions.enable_sms_receipts = value
  }

  get enableEmailReceipts () {
    return this.registerOptions.enable_email_receipts
  }

  set enableEmailReceipts (value) {
    this.registerOptions.enable_email_receipts = value
  }
}
